import type { ReactNode } from 'react';
import type {
  BetStatus,
  BetType,
  GameProviders,
  LogLevel,
  Operator,
  PlayerProfile,
  SortBy,
  TGame,
  TransactionType,
} from '@alien-platform/bo-core';
import type { PlayerProfileType } from '@alien-platform/bo-core/lib/players/types/enum/player.profile.enum';
import { AppJWTPayload, Post } from '@alien-platform/bo-sdk';
import { AdminRoles } from '@alien-platform/bo-sdk-v2';
import { SessionData } from '@auth0/nextjs-auth0/types';
import { InputBaseComponentProps } from '@mui/material';
import { GridSize, ResponsiveStyleValue } from '@mui/system';
import { Path, PathValue } from 'react-hook-form';
import { z } from 'zod';

// import type { CNAMERecord, TXTRecord } from 'cloudflare/resources/dns/records.mjs';

export interface VersionReleases {
  version: string;
  id: string;
  documentId: string;
  releasedAt: string;
  operatorContent: string | null;
  superAdminContent: string | null;
  publishedAt: string | null;
}

export interface UserSession {
  accessToken: string;
  opId: string;
  nickname: string;
  id: string;
  role: AdminRoles;
  email: string;
  timezone?: string;
  roundReportNumbers?: boolean;
}

export enum DateGraph {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST7DAYS = 'last7days',
  LAST30DAYS = 'last30days',
  THISMONTH = 'thismonth',
  LASTMONTH = 'lastmonth',
}
export enum LogsTime {
  CUSTOMDATE = 'customDate',
  TODAY = 'today',
  LAST3DAYS = 'Last3Days',
  LAST7DAYS = 'Last7Days',
  THISWEEK = 'thisWeek',
  THISMONTH = 'thisMonth',
  LASTMONTH = 'LastMonth',
  LAST3MONTH = 'Last3Months',
  LAST6MONTH = 'Last6Months',
  YESTERDAY = 'yesterday',
  ONEMINUTE = 'oneMinute',
  FIVEMINUTE = 'fiveMunite',
  THIRTYMUNITE = 'thirtyMunite',
  ONEHOUR = 'oneHour',
  TWOHOUR = 'twoHour',
  SIXHOUR = 'sixHour',
  LAST30DAYS = 'Last30Days',
}
export enum LogsTimeText {
  CUSTOMDATE = 'Custom Date',
  TODAY = 'Today',
  YESTERDAY = 'Yesterday',
  THISWEEK = 'This Week',
  THISMONTH = 'This Month',
  LASTMONTH = 'Last Month',
  LAST3MONTH = 'Last 3 Months',
  LAST6MONTH = 'Last 6 Months',
  ONEMINUTE = 'Last min',
  FIVEMINUTE = 'Last 5 min',
  THIRTYMUNITE = 'Last 30 min',
  ONEHOUR = 'Last 1 Hour',
  TWOHOUR = 'Last 2 Hours',
  SIXHOUR = 'Last 6 Hours',
  LAST3DAYS = 'Last 3 Days',
  LAST7DAYS = 'Last 7 Days',
  LAST30DAYS = 'Last 30 Days',
}

export enum ReportsTimeText {
  TODAY = 'Today',
  ONEMINUTE = 'Last min',
  FIVEMINUTE = 'Last 5 min',
  THIRTYMUNITE = 'Last 30 min',
  YESTERDAY = 'Yesterday',
  LAST3DAYS = 'Last 3 Days',
  LAST7DAYS = 'Last 7 Days',
  LAST30DAYS = 'Last 30 Days',
  THISWEEK = 'This Week',
  THISMONTH = 'This Month',
  LASTMONTH = 'Last Month',
  LAST3MONTH = 'Last 3 Months',
  LAST6MONTH = 'Last 6 Months',
  CUSTOMDATE = 'Custom Date',
}

export enum FrequentlyText {
  TODAY = 'TODAY',
  YESTERDAY = 'YESTERDAY',
  THISWEEK = 'THISWEEK',
  THISMONTH = 'THISMONTH',
  THISYEAR = 'THISYEAR',
  LAST24HOURS = 'LAST24HOURS',
  LAST48HOURS = 'LAST48HOURS',
  LAST7DAYS = 'LAST7DAYS',
  LAST30DAYS = 'LAST30DAYS',
  LAST90DAYS = 'LAST90DAYS',
  LAST1MINUTE = 'LAST1MINUTE',
  LAST5MINUTE = 'LAST5MINUTE',
  LAST15MINUTE = 'LAST15MINUTE',
  LAST30MINUTE = 'LAST30MINUTE',
  LAST45MINUTE = 'LAST45MINUTE',
  LAST1HOURS = 'LAST1HOURS',
  LAST2HOURS = 'LAST2HOURS',
  LAST6HOURS = 'LAST6HOURS',
  LAST9HOURS = 'LAST9HOURS',
  LAST12HOURS = 'LAST12HOURS',
}

export enum PlayerSubInfo {
  USERNAME = 'USERNAME',
  CONTACT_INFO = 'CONTACT_INFO',
}

export enum AppServiceStates {
  ALL = 'ALL',
  RUNNING = 'RUNNING',
  STOPPED = 'STOPPED',
}

export enum Environments {
  ALL = 'ALL',
  STAGE = 'STAGE',
  RUNNING = 'PREPROD',
  STOPPED = 'PROD',
}

// Define OperatorActions enum with string values
export enum OperatorActions {
  UPDATE_DISPLAY = 'UPDATE_DISPLAY',
  UPDATE_CURRENCIES = 'UPDATE_CURRENCIES',
  UPDATE_LANGS = 'UPDATE_LANGS',
  UPDATE_PROVIDERS = 'UPDATE_PROVIDERS',
  UPDATE_ACCOUNTS = 'UPDATE_ACCOUNTS',
  UPDATE_CMS = 'UPDATE_CMS',
}

export enum PlayerWalletTopupActions {
  TOPUP = 'TOPUP',
  DEDUCT = 'DEDUCT',
}

export enum TableFilter {
  ADMINS = 'ADMINS',
  BETS = 'BETS',
  TRANSACTIONS = 'TRANSACTIONS',
  DOCUMENTS = 'DOCUMENTS',
  GAMES = 'GAMES',
  PLAYERS = 'PLAYERS',
  TREASURY_ACCOUNT = 'TREASURY_ACCOUNT',
  OPERATOR_PLAYERS = 'OPERATOR_PLAYERS',
  OPERATOR_BETS = 'OPERATOR_BETS',
  AFFILIATES = 'AFFILIATES',
  SUPPORT_TICKETS = 'SUPPORT_TICKETS',
  SETTINGS_USERS = 'SETTINGS_USERS',
  DEPOSIT_LIST = 'DEPOSIT_LIST',
  DEPOSIT_PENDING = 'DEPOSIT_PENDING',
  DEPOSIT_ORDERS = 'DEPOSIT_ORDERS',
  DEPOSIT_GATEWAYS = 'DEPOSIT_GATEWAYS',
  DEPOSIT_TREASURIES = 'DEPOSIT_TREASURIES',
  WITHDRAW_LIST = 'WITHDRAW_LIST',
  WITHDRAW_PENDING = 'WITHDRAW_PENDING',
  WITHDRAW_ORDERS = 'WITHDRAW_ORDERS',
  WITHDRAW_GATEWAYS = 'WITHDRAW_GATEWAYS',
  WITHDRAW_TREASURIES = 'WITHDRAW_TREASURIES',
  TOPUP_LIST = 'TOPUP_LIST',
  BANK_ACCOUNTS = 'BANK_ACCOUNTS',
  TRANSACTIONS_LIST = 'TRANSACTIONS_LIST',
  BONUS_LIST = 'BONUS_LIST',
  PENDING_CLAIMS = 'PENDING_CLAIMS',
  TRANSACTIONS_ACTIVITIES = 'TRANSACTIONS_ACTIVITIES',
  PLAYER_DETAILS_ACTIVITIES = 'PLAYER_DETAILS_ACTIVITIES',
  OPERATORS_ACTIVITIES = 'OPERATORS_ACTIVITIES',
  USERS_ACTIVITIES = 'USERS_ACTIVITIES',
  CMS_ACTIVITIES = 'CMS_ACTIVITIES',
  SUPPORT_ACTIVITIES = 'SUPPORT_ACTIVITIES',
  AUTH_ACTIVITIES = 'AUTH_ACTIVITIES',
  BONUS_DEFINITION = 'BONUS_DEFINITION',
  BONUS_RELEASE = 'BONUS_RELEASE',
  INVOICE_LIST = 'INVOICE_LIST',
  APP_SERVICES = 'APP_SERVICES',
  TRANSLATION = 'TRANSLATION',
  CF_ACCOUNTS = 'CF_ACCOUNTS',
  PLAYERS_USERNAMES = 'PLAYERS_USERNAMES',
  TOURNAMENT = 'TOURNAMENT',
  CASHBACK = 'CASHBACK',
  PARTNERS = 'PARTNERS',
}

export interface GetAllAdminsRequestDto {
  user_id?: string;
  email?: string;
  page: number;
  limit: number;
  opId?: string;
  status?: string;
  nickName?: string;
  userCode?: string;
  sortBy?: SortBy[];
  includeBrands?: boolean;
  roles?: string;
}

export interface ProviderConfigProps {
  operator: Operator;
  instanceId: string;
}

export interface AllFilters {
  playerId?: string;
  user_id?: string;
  playerCode?: string;
  status?: BetStatus;
  betId?: string;
  nickName?: string;
  userCode?: string;
  telegramId?: string;
  telegramUsername?: string;
  email?: string;
  gameProvider?: GameProviders;
  gameId?: string;
  betAmount?: number;
  winOdds?: number;
  currency?: string;
  // isTest?: 'isTest' | 'notTest'
  isTest?: boolean;
  fromDate?: string;
  toDate?: string;
  registrationTimeFrom?: string;
  registrationTimeTo?: string;
  activityTimeFrom?: string;
  activityTimeTo?: string;
  isSettled?: boolean;
  isBlocked?: boolean;
  hasDeposit?: boolean;
  // isDeleted?: 'isDeleted' | 'notDeleted'
  isDeleted?: boolean;
  opId?: string;
  hasEmptyBalanceInUSD?: boolean;
  hasPendingVerification?: boolean;
  affId?: string;
  refId?: string;
  channelId?: string;
  provider?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  updatedAtFrom?: string;
  updatedAtTo?: string;
  rtp?: number;
  category?: string;
  title?: string;
  gameTitle?: string;
  aggregator?: string;
  networkName?: string;
  type?: TransactionType;
  hasImageUrl?: boolean;
  ticketId?: string;
  ticketNumber?: string;
  txId?: string;
  includeTags?: string[];
  excludeTags?: string[];
  instance?: string;
  hasPlayed?: boolean;
  hasDeposited?: boolean;
  hasWithdraw?: boolean;
  hasTopup?: boolean;
  isNew?: boolean;
  registrationMethod?: PlayerProfileType;
  chatIsBlocked?: boolean;
  cardNumber?: number;
  playerUid?: number;
  betMode?: BetMode;
  eventId?: string;
  tourId?: string;
  discipline?: string;
  originalProvider?: string;
  betStatus?: BetStatus;
  betType?: BetType | string[];
  minBetAmount?: number;
  maxBetAmount?: number;
  minWinOdds?: number;
  maxWinOdds?: number;
  subType?: string;
  hasRefId?: boolean;

  hasAffId?: boolean;
  txSubType?: string;
  bonusSubType?: string;
  bonusType?: string;
  prizeType?: string;
  txStatus?: string;
  tags?: string[];
  enabledGame?: string[];

  actionMenu?: string;
  isBonusEligible?: boolean;

  areas?: string;
  roles?: string;

  betGameId?: string;
  bonusId?: string;
  bonusStatus?: string;
  bonusDefinitionType?: string;
  isBrand?: boolean;
  isFTD?: boolean;
  bonusBetType?: string;
  isActive?: boolean;
  appName?: string;
  appState?: string;
  appEnv?: string;
  timePeriod?: string[] | string;
  filterBy?: string;
  filterValue?: string;
}

export interface PlayerDetailsProps {
  playerCode: string;
  playerId?: string;
  activeList: string[];
  activeTab?: PlayerTab;
  username?: string;
}

export interface ActivitiesTransactionDetailsProps {
  txId: string;
}

export type CountryMapping = Record<string, string>;

export interface Admin {
  user_id: string;
  email: string;
  opId: string;
  created_at: string;
  updated_at: string;
  last_login: string;
  connection?: unknown;
  email_verified: boolean;
  last_password_reset: string;
  nickname?: string;
  app_metadata?: {
    roles?: string[];
    opId?: string;
    opTitle?: string;
    organization_name?: string;
    nickname?: string;
    userCode?: string;
    language?: string;
    timezone?: string;
    domains?: string[];
    roundReportNumbers?: boolean;
    playerSubInfoType?: PlayerSubInfo;
    showEventTitleInBetsList?: boolean;
    parentOpId?: string;
    parentOpTitle?: string;
  };
  blocked?: boolean;
  permissions: Permissions;
}

export interface DnsRecord {
  id: string;
  zone_id: string;
  zone_name: string;
  name: string;
  type: string;
  content: string;
  proxiable: boolean;
  proxied: boolean;
  ttl: number;
  settings: object;
  meta: object;
  comment?: string;
  created_on: string;
  modified_on: string;
}
export interface AlienDomainDns {
  id: string;
  zone_id: string;
  zone_name: string;
  name: string;
  type: string;
  content: string;
  proxiable: string;
  proxied: string;
}
export interface CfAccount {
  title: string;
  opId: string;
  opTitle: string;
  cfAccountId: string;
  cfApiKey: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  note: string;
  extraData: object;
}
export interface AlienDomain {
  domainName: string;
  opId: string;
  opTitle: string;
  cfAccountId: string;
  cfZoneNameServers: string[];
  cfDnsRecords: AlienDomainDns[];
  cfExtraData: unknown[];
  afdExtraData: unknown[];
  createdAt: string;
  updatedAt: string;
  cfZoneSSLMode: string;
  cfZoneStatus: string;
  cfZoneId: string;
}
export interface AlienAfd {
  resourceId: string;
  resourceGroup: string;
  afdProfileName: string;
  isDefault: boolean;
  title: string;
  description: string;
  note: string;
  createdAt: string;
  updatedAt: string;
}

export interface DataFetchResponse<T> {
  success: boolean;
  status: string;
  data: T;
  count: number;
  remaining: number;
  error?: string;
}

export interface GetAdminsResponse {
  total: number;
  limit: number;
  admins: Admin[];
}

export interface ElasticLog {
  data: unknown;
  title?: string;
  session?: SessionData;
}

export interface LogToCreate {
  level: LogLevel;
  location: string;
  title: string;
  type: string;
  subtype: string;
  message: string;
  description: string;
  logData: LogData;
  meta: unknown;
}

export interface LogData {
  uniqueId?: string;

  ip?: string;

  country?: string;

  headerHost?: unknown;
  headerIp?: string;
  headerCountry?: string;

  userAgent?: string;
  origin?: string;
  referer?: string;

  cfConnectingIp?: string | string[];
  cfIpCountry?: string | string[];

  opId?: string;
  opTitle?: string;
  opFullId?: string;

  playerCode?: string;
  playerFullId?: string;
  playerId?: string;

  balance?: number;

  affId?: string;
  refId?: string;
  channelId?: string;

  currency?: string;

  gameData?: unknown;
  gameId?: string;
  gameTitle?: string;

  brandId?: string;
  brandName?: string;

  paymentProvider?: string;

  userEmail?: string;
  userCode?: string;
  userId?: string;
  userOpId?: string;
  userNickname?: string;
  userRoles?: string[];
}

export interface ReportTableCustomType {
  pl: { date: string; value: number; count?: number };
  bets: { date: string; value: number; count?: number };
  ftd: { date: string; value: number; count?: number };
  registrations: { date: string; value: number; count?: number };
}
export type GameReportTableCustomType = Record<
  string,
  {
    provider?: string;
    betAmount?: number;
    winAmount?: number;
    pl?: number;
    betsCount?: number;
    margin?: number;
  }[]
>;

// export interface Affiliate {
//   user_id: string;
//   email: string;
//   nickname: string;
//   password: string;
//   opId: string;
//   onCasino: number;
//   onSportsBook: number;
//   onDeposit: number;
//   onWithdraw: number;
//   onReferralCommission: number;
//   administrationCommission: number;
//   app_metadata?: unknown;
//   created_at: string;
//   last_login: string;
//   blocked: boolean;
//   onReferral?: number;
//   administration?: number;
//   affId?: string;
//   permissions: Permissions;
//   updated_at: string;
//   email_verified: boolean;
//   last_password_reset: string;
// }

export interface Affiliate {
  affId?: string;
  affChannelId?: string;
  timestamp?: number;
  isUsed?: boolean;
}

export enum PermissionsItem {
  COMPONENT = 'COMPONENT',
  MENU = 'MENU',
}
export interface PermissionDetails {
  title?: string;
  name?: string;
  desc?: string;
  image?: string;
  access: boolean;
  tags?: string[];
  requiredMenuItems?: string[];
  extra?: Record<string, unknown>;
  isSuperAdmin?: boolean;
  allowedRoles?: AdminRoles[];
  updatedAt?: Date;
}

export type Permissions = Record<string, PermissionDetails>;

export interface LogInterface {
  type: string;
  subType: string;
  message: string;
  location: string;
  data: unknown;
  title: string;
  description?: string;
  session?: SessionData;
}

export interface AffiliateReportsHelpInfo {
  cards: {
    totalProfit: string;
    totalEstimatedProfit: string;
    registered: string;
    activePlayers: string;
    currentMonthProfitBalance: string;
    estimatedCurrentMonthBalance: string;
    affiliateProfitInUSD: string;
    operatorProfitInUSD: string;
  };
  reports: {
    level: string;
    cpr: string;
    cpa: string;
    signups: string;
    claimedBonusAmount: string;
    revenueShare: string;
    withdrawals: string;
    referredPlayersWager: string;
    referredPlayersDeposit: string;
    referredPlayersWithdrawal: string;
  };
  affiliate: {
    minWithdrawableAmountInUSD: string;
    minRequiredWagerInUSDToConsiderAPlayerAsActive: string;
    claimedBonusAdminFeePercentage: string;
    cashbackAdminFeePercentage: string;
    affiliatesAdminFeePercentage: string;
  };
  levelSettings: {
    level: string;
    emailCpr: string;
    gmailCpr: string;
    telegramCpr: string;
    maxCpr: string;
    cpa: string;
    requiredWagerInUSD: string;
  };
  revenueShareSettings: {
    monthlyReferredPlayersTotalWagerInUSD: string;
    revenueSharePercentage: string;
  };
  effectiveAffiliateNGR: string[];
  affiliateCommissionInUSD: string;
}

export interface AffiliateSettingsHelp {
  affiliate: {
    minWithdrawableAmountInUSD: string;
    minRequiredWagerInUSDToConsiderAPlayerAsActive: string;
    claimedBonusAdminFeePercentage: string;
    cashbackAdminFeePercentage: string;
    affiliatesAdminFeePercentage: string[];
  };
  levelSettings: {
    level: string;
    emailCpr: string;
    gmailCpr: string;
    telegramCpr: string;
    maxCpr: string;
    cpa: string;
    requiredWagerInUSD: string;
  };
  revenueShareSettings: {
    monthlyReferredPlayersTotalWagerInUSD: string;
    revenueSharePercentage: string;
  };
  effectiveAffiliateNGR: string[];
  affiliateCommissionInUSD: string;
}

export interface CashbackDescriptions {
  cashbackClaimed: string;
  cashbackAvailableToClaim: string;
  totalLoss: string;
  adjustedLossInUSD: string;
  playerLossInUSD: string;
  betAmountsInUSD: string;
  bonusClaimedInUSD: string;
  settings: {
    lostAmount: string;
    percentage: string;
  };
}

export interface GeneralGameDetailsProps {
  game: TGame;
  operators: Operator[];
}

export interface OperatorGameDetailsProps {
  game: TGame;
  opId: string;
}

export enum AdminStatus {
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED',
}

export interface NewBrandDto {
  opId: string;
  parentOpId: string;
  domains: string[];
  title: string;
  cfApiToken: string;
  zoneId: string;
}

export interface PaginatedResponse {
  items: Post[];
  itemsPerPage: number;
  page?: number | undefined;
  pages: number;
  totalItems: number;
}

export interface MediaItemProps {
  custom_key: string;
  title?: string;
  image?: string;
  url?: string;
  file: File | undefined;
  tags?: string[];
  type?: string;
  supportedFormats?: string;
  dimensions?: string;
  direction?: 'row' | 'column';
}
export interface BannerProps {
  title?: string;
  subtitle?: string;
  category?: string;
  extraData?: object;
  image?: string;
  url: string;
  file: File | undefined;
  tags?: string[];
  type?: string;
}
export enum UpdateOperatorConfig {
  DOMAINS = 'DOMAINS',
  DISPLAY = 'DISPLAY',
  AUTH = 'AUTH',
  CURRENCIES = 'CURRENCIES',
  LANGS = 'LANGS',
  PROVIDERS = 'PROVIDERS',
  ACCOUNTS = 'ACCOUNTS',
  WAGER_POLICY = 'WAGER_POLICY',
  TIERS = 'TIERS',
  TOPUP_LIMITS = 'TOPUP_LIMITS',
  SETTINGS = 'SETTINGS',
  DEPOSIT_SETTINGS = 'DEPOSIT_SETTINGS',
  WITHDRAW_SETTINGS = 'WITHDRAW_SETTINGS',
  PASSWORD_RECOVERY = 'PASSWORD_RECOVERY',
  IN_GAME_CURRENCY = 'IN_GAME_CURRENCY',
  IT_TURBO_STARS_VERSION = 'IT_TURBO_STARS_VERSION',
  TRANSFORMATION = 'TRANSFORMATION',
  AVATAR_IMAGE = 'AVATAR_IMAGE',
  TELEGRAM_SETTINGS = 'TELEGRAM_SETTINGS',
  SMS_PROVIDER = 'SMS_PROVIDER',
}

export enum ProgressStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
}
export enum BetMode {
  COMBO = 'COMBO',
  SINGLE = 'SINGLE',
}

type SearchParams = Promise<Record<string, string | undefined>>;

export interface PageProps {
  params: { lng: string; slug: string };
  searchParams: SearchParams;
}

export enum WalletAddressError {
  ISREQUIRED = 'ISREQUIRED',
  ISINVALID = 'ISINVALID',
}

export enum EmailError {
  ISREQUIRED = 'ISREQUIRED',
  ISINVALID = 'ISINVALID',
}

export enum PasswordError {
  ISREQUIRED = 'ISREQUIRED',
  MIN8CHARACTER = 'MIN8CHARACTER',
  ONEUPERONELOWERCASE = 'ONEUPERONELOWERCASE',
  CONTAINSDIGITS = 'CONTAINSDIGITS',
}

export enum ChannelTab {
  DETAILS = 'DETAILS',
  REPORT = 'REPORT',
}
export enum AffiliateTab {
  DETAILS = 'DETAILS',
  AFFILIATE_REPORTS = 'AFFILIATE_REPORTS',
  AFFILIATE_BILLING = 'AFFILIATE_BILLING',
  ACTIONS = 'ACTIONS',
  CHANNELS = 'CHANNELS',
  PERMISSIONS = 'PERMISSIONS',
  SETTINGS = 'SETTINGS',
  SETTINGS_REPORT = 'SETTINGS_REPORT',
  PROFIT_STATS = 'PROFIT_STATS',
  REPORTS = 'REPORTS',
}

export enum PlayerTab {
  DETAILS = 'DETAILS',
  DETAILSJSON = 'DETAILSJSON',
  BETS = 'BETS',
  TRANSACTIONS = 'TRANSACTIONS',
  REPORTS = 'REPORTS',
  REPORTS_V2 = 'REPORTS_V2',
  REFERRAL_REPORTS = 'REFERRAL_REPORTS',
  AFFILIATE_REPORTS = 'AFFILIATE_REPORTS',
  AFFILIATE_CHANNELS = 'AFFILIATE_CHANNELS',
  SPORT_FREEBETS = 'SPORT_FREEBETS',
  BONUS_REPORTS = 'BONUS_REPORTS',
  ACTIVITIES = 'ACTIVITIES',
  DOCUMENTS = 'DOCUMENTS',
  PROFIT = 'PROFIT',
  NOTE = 'NOTE',
  ACTIONS = 'ACTIONS',
  WALLET = 'WALLET',
  LOGS = 'LOGS',
  SUPPORT = 'SUPPORT',
  ADMIN_NOTES = 'ADMIN_NOTES',
  TECH_SUPPORT = 'TECH_SUPPORT',
  QA = 'QA',
  STATS = 'STATS',
  CASHBACK = 'CASHBACK',
  KYC = 'KYC',
  SETTINGS = 'SETTINGS',
  KYC_V2 = 'KYC_V2',
}
export enum RiskManagementTab {
  PLAYERS = 'PLAYERS',
  GAMES = 'GAMES',
}

export enum TemplateTab {
  BRANDING = 'BRANDING',
  BANNERS = 'BANNERS',
  FEATURED_ARTICLE = 'FEATURED_ARTICLE',
  FEATURED_CARDS = 'FEATURED_CARDS',
  TOURNAMENT_TAB = 'TOURNAMENT_TAB',
  FOOTER_TAB = 'FOOTER_TAB',
  JSON_DETAILS = 'JSON_DETAILS',
  DNS_TAB = 'DNS_TABS',
  TRANSLATION_TAB = 'TRANSLATION_TAB',
  FRONTDOOR_TAB = 'FRONTDOOR_TAB',
  LOGS_TAB = 'LOGS_TAB',
  AUTH0_TAB = 'AUTH0_TAB',
  BONUS_SETTINGS_TAB = 'BONUS_SETTINGS_TAB',
  BONUS_STYLES_TAB = 'BONUS_STYLES_TAB',
  ZONE_DETAILS_TAB = 'ZONE_DETAILS_TAB',
  ZONE_DNS_TAB = 'ZONE_DNS_TAB',
  ZONE_ACTIONS_TAB = 'ZONE_ACTIONS_TAB',
  OPERATOR_DOMAINS_TAB = 'OPERATOR_DOMAINS_TAB',
  ROLES_TAB = 'ROLES_TAB',
  PERMISSIONS_TAB = 'PERMISSIONS_TAB',
  APP_SERVICE_TAB = 'APP_SERVICE_TAB',
  BRANDING_GENERAL_TAB = 'BRANDING_GENERAL_TAB',
  BRANDING_THEME_TAB = 'BRANDING_THEME_TAB',
  WIDGETS = 'WIDGETS',
}

export enum AdminTab {
  DETAILS = 'DETAILS',
  ACTIONS = 'ACTIONS',
  PERMISSIONS = 'PERMISSIONS',
  JSON = 'JSON',
  LOGS = 'LOGS',
}
export enum RolesTabs {
  ROLES = 'ROLES',
  PERMISSIONS = 'PERMISSIONS',
}
export enum NewBrandTab {
  ACCOUNT_DETAILS = 'ACCOUNT_DETAILS',
  COMMISSION_STRUCTURE = 'COMMISSION_STRUCTURE',
  BRAND_WEBSITE = 'BRAND_WEBSITE',
  AUTH_SETUP = 'AUTH_SETUP',
  LANGUAGE_AND_CURRENCY = 'LANGUAGE_AND_CURRENCY',
  CMS_SETUP = 'CMS_SETUP',
  SLIDER = 'SLIDER',
  CARDS_SECTION = 'CARDS_SECTION',
  FOOTER = 'FOOTER',
  SEO = 'SEO',
  NAME_SERVERS = 'NAME_SERVERS',
}
export enum PermissionsSections {
  ALL = 'ALL',
  PLAYERS = 'PLAYERS',
  BETS = 'BETS',
  DEPOSIT = 'DEPOSIT',
  WITHDRAW = 'WITHDRAW',
  OPERATORS = 'OPERATORS',
  BONUS = 'BONUS',
  TOURNAMENT = 'TOURNAMENT',
  STAFF_SETTINGS = 'STAFF_SETTINGS',
}
export enum OperatorTab {
  DETAILS = 'DETAILS',
  DETAILSJSON = 'DETAILSJSON',
  GENERALCONFIG = 'GENERALCONFIG',
  SETTINGS = 'SETTINGS',
  UI = 'UI',
  DOMAINS = 'DOMAINS',
  PAYMENTGATEWAYS = 'PAYMENTGATEWAYS',
  CRYPTOPROVIDERS = 'CRYPTOPROVIDERS',
  DEPOSIT = 'DEPOSIT',
  WITHDRAW = 'WITHDRAW',
  CMS = 'CMS',
  GAMES = 'GAMES',
  PLAYERS = 'PLAYERS',
  BETS = 'BETS',
  SUPPORT = 'SUPPORT',
  BRANDS = 'BRANDS',
  TIERS = 'TIERS',
  WAGERING = 'WAGERING',
  REFERRAL_SETTINGS = 'REFERRAL_SETTINGS',
  AFFILIATE_SETTINGS = 'AFFILIATE_SETTINGS',
  BILLING_SETTINGS = 'BILLING_SETTINGS',
  ADMINISTRATION_SETTINGS = 'ADMINISTRATION_SETTINGS',
  CASHBACK_SETTINGS = 'CASHBACK_SETTINGS',
}

export enum GamesTab {
  TABLE = 'TABLE',
  GRID = 'GRID',
}

export enum PaymentProviderTab {
  DETAILS = 'DETAILS',
  GENERAL_CONFIG = 'GENERAL_CONFIG',
  OPTIONS = 'OPTIONS',
  DESTINATION_SETTINGS = 'DESTINATION_SETTINGS',
  CONVERSION_SETTINGS = 'CONVERSION_SETTINGS',
  DEPOSIT_PACKAGES = 'DEPOSIT_PACKAGES',
  WITHDRAW_PACKAGES = 'WITHDRAW_PACKAGES',
  APPROVAL_SETTINGS = 'APPROVAL_SETTINGS',
  SECRETS = 'SECRETS',
  CREDENTIALS = 'CREDENTIALS',
  VOUCHER = 'VOUCHER',
  JSON = 'JSON',
  EDIT = 'EDIT',
  CONDITIONS = 'CONDITIONS',
  LIMITS = 'LIMITS',
  BONUS = 'BONUS',
  BONUS_JSON = 'BONUS_JSON',
  REPORT = 'REPORT',
  ACTIONS = 'ACTIONS',
}

export enum CfAccountDetailsTab {
  DETAILS = 'DETAILS',
  DOMAINS = 'DOMAINS',
  JSON = 'JSON',
}
export enum DomainDetailsTab {
  DETAILS = 'DETAILS',
  DNS = 'DNS',
  ACTIONS = 'ACTIONS',
  JSON = 'JSON',
}

export enum PaymentGatewayTab {
  DETAILSJSON = 'DETAILSJSON',
  GENERALCONFIG = 'GENERALCONFIG',
  SETTINGS = 'SETTINGS',
}

export enum PaymentCategoriesTab {
  DEPOSIT = 'DEPOSIT',
  WITHDRAW = 'WITHDRAW',
}

export enum OperatorUITemplate {
  FULL = 'FULL',
  MINIMAL = 'MINIMAL',
}

export enum GeneralDrawer {
  BET_DETAILS = 'BET_DETAILS',
  CHANNEL_DETAILS = 'CHANNEL_DETAILS',
  GENERAL_GAME_DETAILS = 'GENERAL_GAME_DETAILS',
  OPERATOR_GAME_DETAILS = 'OPERATOR_GAME_DETAILS',
  PLAYER_DETAILS = 'PLAYER_DETAILS',
  PLAYER_SUPPORT_TICKET_DETAILS = 'PLAYER_SUPPORT_TICKET_DETAILS',
  TABLE_FILTERS = 'TABLE_FILTERS',
  TRANSACTION_DETAILS = 'TRANSACTION_DETAILS',
  SUPPORT_TICKET_DETAILS = 'SUPPORT_TICKET_DETAILS',
  SUPPORT_TICKET_TEMPLATES = 'SUPPORT_TICKET_TEMPLATES',
  NAV_DRAWER = 'NAV_DRAWER',
  OPERATOR_DETAILS = 'OPERATOR_DETAILS',
  ADMIN_DETAILS = 'ADMIN_DETAILS',
  AFFILIATE_DETAILS = 'AFFILIATE_DETAILS',
  NEW_AFFILIATE = 'NEW_AFFILIATE',
  NEW_ADMIN = 'NEW_ADMIN',
  NEW_DEPOSIT_GATEWAY = 'NEW_DEPOSIT_GATEWAY',
  NEW_DEPOSIT_GATEWAY_V2 = 'NEW_DEPOSIT_GATEWAY_V2',
  NEW_WITHDRAW_GATEWAY_V2 = 'NEW_WITHDRAW_GATEWAY_V2',
  NEW_TAG = 'NEW_TAG',
  FEATURED_CARD_DETAILS = 'FEATURED_CARD_DETAILS',
  SELECTED_BANNER_DETAILS = 'SELECTED_BANNER_DETAILS',
  SELECTED_CMS_MODAL_DETAILS = 'SELECTED_CMS_MODAL_DETAILS',
  REFERRAL_CLAIM_HISTORY = 'REFERRAL_CLAIM_HISTORY',
  AFFILIATE_CLAIM_HISTORY = 'AFFILIATE_CLAIM_HISTORY',
  AFFILIATE_WITHDRAWAL_HISTORY = 'AFFILIATE_WITHDRAWAL_HISTORY',
  REFERRED_PLAYERS_LIST = 'REFERRED_PLAYERS_LIST',
  OPERATOR_BILLING_RECORD_DETAILS = 'OPERATOR_BILLING_RECORD_DETAILS',
  BRAND_BILLING_RECORD_DETAILS = 'BRAND_BILLING_RECORD_DETAILS',
  AFFILIATE_BILLING_RECORD_DETAILS = 'AFFILIATE_BILLING_RECORD_DETAILS',
  OPERATOR_PAYMENT_FORM = 'OPERATOR_PAYMENT_FORM',
  OPERATOR_GENERATE_INVOICE_FORM = 'OPERATOR_GENERATE_INVOICE_FORM',
  DNS_RECORD = 'DNS_RECORD',
  BRAND_PAYMENT_FORM = 'BRAND_PAYMENT_FORM',
  BRAND_GENERATE_INVOICE_FORM = 'BRAND_GENERATE_INVOICE_FORM',
  AFD_DETAILS = 'AFD_DETAILS',
  PLAYER_BONUS_DETAILS = 'PLAYER_BONUS_DETAILS',
  PLAYER_BONUS_RELEASE_DETAILS = 'PLAYER_BONUS_RELEASE_DETAILS',
  OPERATOR_AFFILIATE_PAYMENT_FORM = 'OPERATOR_AFFILIATE_PAYMENT_FORM',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  AFFILATE_MONTHLY_STATS_DETAILS = 'AFFILATE_MONTHLY_STATS_DETAILS',
  CF_TOKEN_DETAILS = 'CF_TOKEN_DETAILS',
  SPORT_FREEBET_DETAILS = 'SPORT_FREEBET_DETAILS',
  SPORT_FREEBET_TEMPLATE_LIST = 'SPORT_FREEBET_TEMPLATE_LIST',
  PLAYER_APPLIED_FREEBET_DETAILS = 'PLAYER_APPLIED_FREEBET_DETAILS',
  CF_ZONE_DETAILS = 'CF_ZONE_DETAILS',
  ROLE_DETAILS = 'ROLE_DETAILS',
  PLAY_GAME = 'PLAY_GAME',
  BONUS_RELEASE_REPORT_DETAILS = 'BONUS_RELEASE_REPORT_DETAILS',
  ACTIVITIES_TRANSACTION_DETAILS = 'ACTIVITIES_TRANSACTION_DETAILS',
  AFFILIATE_ACTIVE_PLAYERS = 'AFFILIATE_ACTIVE_PLAYERS',
  OPERATOR_BILLING_INVOICE_DETAILS = 'OPERATOR_BILLING_INVOICE_DETAILS',
  PLAYER_CASHBACK_TRANSACTIONS_LIST = 'PLAYER_CASHBACK_TRANSACTIONS_LIST',
  PLAYER_PROFIT_TRANSACTIONS_LIST = 'PLAYER_PROFIT_TRANSACTIONS_LIST',
  DOMAIN_DETAILS = 'DOMAIN_DETAILS',
  CF_ACCOUNT_DETAILS = 'CF_ACCOUNT_DETAILS',
  CF_ZONE_DETAILS_V2 = 'CF_ZONE_DETAILS_V2',
  STAFF_DETAILS = 'STAFF_DETAILS',
  KYC_DETAILS = 'KYC_DETAILS',
  NEW_TOURNAMENT = 'NEW_TOURNAMENT',
  NEW_BONUS_DEFINITION = 'NEW_BONUS_DEFINITION',
  APPEARANCE = 'APPEARANCE',
}

export enum GeneralModal {
  PLAYER_DELETE_CONFIRMATION = 'PLAYER_DELETE_CONFIRMATION',
  VERIFY_DEPOSIT_CONFIRMATION = 'VERIFY_DEPOSIT_CONFIRMATION',
  APPROVE_REJECT_WITHDRAW_CONFIRMATION = 'APPROVE_REJECT_WITHDRAW_CONFIRMATION',
  APPROVE_REFERRAL_CLAIM_CONFIRMATION = 'APPROVE_REFERRAL_CLAIM_CONFIRMATION',
  EDIT_OPERATOR_BILLING_RECORD_PAYMENT = 'EDIT_OPERATOR_BILLING_RECORD_PAYMENT',
  EDIT_OPERATOR_BILLING_RECORD_INVOICE = 'EDIT_OPERATOR_BILLING_RECORD_INVOICE',
  EDIT_OPERATOR_BILLING_RECORD_INVOICE_V2 = 'EDIT_OPERATOR_BILLING_RECORD_INVOICE_V2',
  DELETE_OPERATOR_BILLING_RECORD_PAYMENT = 'DELETE_OPERATOR_BILLING_RECORD_PAYMENT',
  DELETE_OPERATOR_BILLING_RECORD_INVOICE = 'DELETE_OPERATOR_BILLING_RECORD_INVOICE',
  EDIT_BRAND_BILLING_RECORD_PAYMENT = 'EDIT_BRAND_BILLING_RECORD_PAYMENT',
  DELETE_BRAND_BILLING_RECORD_INVOICE = 'DELETE_BRAND_BILLING_RECORD_INVOICE',
  EDIT_BRAND_BILLING_RECORD_INVOICE = 'EDIT_BRAND_BILLING_RECORD_INVOICE',
  DELETE_BRAND_BILLING_RECORD_PAYMENT = 'DELETE_BRAND_BILLING_RECORD_PAYMENT',
  EDIT_AFFILIATE_BILLING_RECORD_PAYMENT = 'EDIT_AFFILIATE_BILLING_RECORD_PAYMENT',
  DELETE_AFFILIATE_BILLING_RECORD_PAYMENT = 'DELETE_AFFILIATE_BILLING_RECORD_PAYMENT',
  DELETE_TRANSACTION = 'DELETE_TRANSACTION',
  APPROVE_REJECT_BANK_ACCOUNT_CONFIRMATION = 'APPROVE_REJECT_BANK_ACCOUNT_CONFIRMATION',
  CONVERT_PLAYER_BALANCE = 'CONVERT_PLAYER_BALANCE',
  CHECK_ORDER_STATUS = 'CHECK_ORDER_STATUS',
  REJECT_DEPOSIT_ORDER = 'REJECT_DEPOSIT_ORDER',
  ADD_SPORT_FREEBET_CONFIRMATION = 'ADD_SPORT_FREEBET_CONFIRMATION',
  APPROVE_REJECT_BONUS_CONFIRMATION = 'APPROVE_REJECT_BONUS_CONFIRMATION',
  APPROVE_REJECT_CASHBACK_CONFIRMATION = 'APPROVE_REJECT_CASHBACK_CONFIRMATION',
  APPROVE_REJECT_AFFILIATE_CONFIRMATION = 'APPROVE_REJECT_AFFILIATE_CONFIRMATION',
  OWN_USER_RELOGIN = 'OWN_USER_RELOGIN',
  STAFF_RELOGIN = 'STAFF_RELOGIN',
  FORCE_APPROVE_JB_WITHDRAW = 'FORCE_APPROVE_JB_WITHDRAW',
  INFORMATION_ICON = 'INFORMATION_ICON',
  HELP_INFO = 'HELP_INFO',
  LAUNCH_GAME_URL = 'LAUNCH_GAME_URL',
  ERROR_RESPONSE = 'ERROR_RESPONSE',
  AFFILIATE_WITHDRAW_PROFIT_CONFIRMATION = 'AFFILIATE_WITHDRAW_PROFIT_CONFIRMATION',
  PLAYER_SEARCH = 'PLAYER_SEARCH',
  APPROVE_TOMANI_WITHDRAW = 'APPROVE_TOMANI_WITHDRAW',
  DELETE_DEPOSIT_GATEWAY_CONFIRMATION = 'DELETE_DEPOSIT_GATEWAY_CONFIRMATION',
  DELETE_WITHDRAW_GATEWAY_CONFIRMATION = 'DELETE_WITHDRAW_GATEWAY_CONFIRMATION',
  EDIT_OPERATOR_BILLING_INVOICE_SETTING = 'EDIT_OPERATOR_BILLING_INVOICE_SETTING',
  EDIT_OPERATOR_BILLING_INVOICE = 'EDIT_OPERATOR_BILLING_INVOICE',
  OPERATOR_GENERATE_INVOICE_FORM = 'OPERATOR_GENERATE_INVOICE_FORM',
  OPERATOR_BILLING_PROVIDER_PAYMENT = 'OPERATOR_BILLING_PROVIDER_PAYMENT',
  PLAYER_CASHBACK_CLAIM_CONFIRMATION = 'PLAYER_CASHBACK_CLAIM_CONFIRMATION',
  PLAYER_CASHBACK_OVERRIDE_SETTINGS = 'PLAYER_CASHBACK_OVERRIDE_SETTINGS',
  PLAYER_AFFILIATE_OVERRIDE_SETTINGS = 'PLAYER_AFFILIATE_OVERRIDE_SETTINGS',
  AFFILIATE_PROFIT_CALCULATOR = 'AFFILIATE_PROFIT_CALCULATOR',
  PLAYER_TEST_CALCULATE_AFFILIATE_PROFIT = 'PLAYER_TEST_CALCULATE_AFFILIATE_PROFIT',
  AFFILIATE_LEVEL_SETTINGS = 'AFFILIATE_LEVEL_SETTINGS',
  PLAYER_CASHBACK_RESTORE_OPERATOR_SETTINGS = 'PLAYER_CASHBACK_RESTORE_OPERATOR_SETTINGS',
  PLAYER_AFFILIATE_RESTORE_OPERATOR_SETTINGS = 'PLAYER_AFFILIATE_RESTORE_OPERATOR_SETTINGS',
}

export enum ModalDrawer {
  ALERT = 'ALERT',
  WALLET = 'WALLET',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  FORGOTPASSWORD = 'FORGOTPASSWORD',
  NOTDETECTED = 'NOTDETECTED',
  PROCESSING = 'PROCESSING',
  TELEGRAM = 'TELEGRAM',
  // LOGINREGISTER = 'LOGINREGISTER',
  SUPPORT = 'SUPPORT',
  POPUP = 'POPUP',
  RELOADGAME = 'RELOADGAME',
  MENU = 'MENU',
  TOURNAMENTS = 'TOURNAMENTS',
  CASINO = 'CASINO',
  SPORT = 'SPORT',
  BETS = 'BETS',
  FAVORITES = 'FAVORITES',
  TRANSACTIONS = 'TRANSACTIONS',
  TRANSACTIONDETAILS = 'TRANSACTIONDETAILS',
  SOON = 'SOON',
  NOTIFICATIONS = 'NOTIFICATIONS',
  FILTERS = 'FILTERS',
  MYPROFILE = 'MYPROFILE',
  REFFERAL = 'REFFERAL',
  VERIFICATION = 'VERIFICATION',
  BETDETAILS = 'BETDETAILS',
  PLAYERDETAILS = 'PLAYERDETAILS',
  KYC = 'KYC',
  DELETEPLAYER = 'DELETEPLAYER',
  TOPUP = 'TOPUP',
  TOPUP_BONUS = 'TOPUP_BONUS',
  NOTFOUND = 'NOTFOUND',
  METAMASK = 'METAMASK',
  BROWSERNOTSUPPORTED = 'BROWSERNOTSUPPORTED',
  BOWALLETDETAILS = 'BOWALLETDETAILS',
  GAMEINFO = 'GAMEINFO',
  BOTRANSACTIONDETAILS = 'BOTRANSACTIONDETAILS',
  BOBETDETAILS = 'BOBETDETAILS',
  SHOWIMG = 'SHOWIMG',
  BOAFFILIATEDETAILS = 'BOAFFILIATEDETAILS',
  CRYPTO_PROVIDER_GENERAL_CONFIG = 'CRYPTO_PROVIDER_GENERAL_CONFIG',
  NEWCHANNEL = 'NEWCHANNEL',
  NEWAFFILIATE = 'NEWAFFILIATE',
  CHANNELCREATED = 'CHANNELCREATED',
  CMSPAGE_EDITOR = 'CMSPAGE_EDITOR',
  OPERATOR_GAME_DETAILS = 'OPERATOR_GAME_DETAILS',
  BETSFILTER = 'BETSFILTER',
  TRANSACTIONSFILTER = 'TRANSACTIONSFILTER',
  TABLEFILTERS = 'TABLEFILTERS',
  CHANNELDETAILS = 'CHANNELDETAILS',
  CREATE_SUPPORT_TICKET = 'CREATE_SUPPORT_TICKET',
  ENABLE_GAME_FOR_OPERATOR = 'ENABLE_GAME_FOR_OPERATOR',
  CHANGE_PASSWORD = 'CHANGE_PASSWORD',
  UPDATE_TICKET_STATUS = 'UPDATE_TICKET_STATUS',
  BLOCK_USER = 'BLOCK_USER',
  DELETE_USER = 'DELETE_USER',
  DELETE_AFFILIATE = 'DELETE_AFFILIATE',
  CHANGE_ROLES = 'CHANGE_ROLES',
  DELETE_NOTIFICATION = 'DELETE_NOTIFICATION',
  DELTE_FLOW = 'DELTE_FLOW',
  DELETE_BRAND = 'DELETE_BRAND',
  DELETE_PAGE = 'DELETE_PAGE',
  DELETE_PAGE_TYPE = 'DELETE_PAGE_TYPE',
  UPLOAD_ASSET = 'UPLOAD_ASSET',
  MEDIA_PICKER = 'MEDIA_PICKER',
  PAGE_PICKER = 'PAGE_PICKER',
  DELETE_MENU = 'DELETE_MENU',
  DEPOSIT_SETTINGS_CALCULATOR = 'DEPOSIT_SETTINGS_CALCULATOR',
  WITHDRAW_SETTINGS_CALCULATOR = 'WITHDRAW_SETTINGS_CALCULATOR',
  REFRESH_PERMISSIONS = 'REFRESH_PERMISSIONS',
  SETTLE_OPEN_BET = 'SETTLE_OPEN_BET',
  DELETE_SLIDE = 'DELETE_SLIDE',
  CREATE_DNS_RECORD = 'CREATE_DNS_RECORD',
  AUTOMATION_PROGRESS = 'AUTOMATION_PROGRESS',
  DELETE_CHANNEL = 'DELETE_CHANNEL',
  DELETE_AUTH0_DOMAIN = 'DELETE_AUTH0_DOMAIN',
  ADD_AUTH0_DOMAIN = 'ADD_AUTH0_DOMAIN',
  DELETE_AUTH0_ORG = 'DELETE_AUTH0_ORG',
  ADD_AUTH0_ORG = 'ADD_AUTH0_ORG',
  DELETE_BONUS = 'DELETE_BONUS',
  DELETE_BONUS_RELEASE = 'DELETE_BONUS_RELEASE',
  PLAYER_USERNAME_CONFIRMATION = 'PLAYER_USERNAME_CONFIRMATION',
  ADD_CF_TOKEN = 'ADD_CF_TOKEN',
  DELETE_CF_TOKEN = 'DELETE_CF_TOKEN',
  ADD_OPERATOR_DOMAIN = 'ADD_OPERATOR_DOMAIN',
  GAMES_PICKER = 'GAMES_PICKER',
  ADD_CF_DNS_RECORD = 'ADD_CF_DNS_RECORD',
  DELETE_DNS_RECORD_TOKEN = 'DELETE_DNS_RECORD_TOKEN',
  ADD_OPERATOR_DOMAIN_MINIMIZED = 'ADD_OPERATOR_DOMAIN_MINIMIZED',
  NOTIFICATION_MODAL = 'NOTIFICATION_MODAL',
  CREATE_PERMISSION = 'CREATE_PERMISSION',
  CLONE_BONUS = 'CLONE_BONUS',
  DELETE_TRANSLATION = 'DELETE_TRANSLATION',
  CREATE_TRANSLATION = 'CREATE_TRANSLATION',
  DELETE_MEDIA_ITEM = 'DELETE_MEDIA_ITEM',
  DELETE_PERMISSION = 'DELETE_PERMISSION',
  TRANSFORM_OPERATOR = 'TRANSFORM_OPERATOR',
  DELETE_ROLE = 'DELETE_ROLE',
  REJECT_KYC = 'REJECT_KYC',
  DELETE_PERMISSION_TEMPLATE = 'DELETE_PERMISSION_TEMPLATE',
  CLONE_GAMES = 'CLONE_GAMES',
  CLONE_PERMISSIONS = 'CLONE_PERMISSIONS',
  DELETE_CF_ZONE = 'DELETE_CF_ZONE',
  DELETE_AFD_DOMAIN = 'DELETE_AFD_DOMAIN',
  CREATE_AFD_DOMAIN = 'CREATE_AFD_DOMAIN',
  APP_SERVICE_METRICS = 'APP_SERVICE_METRICS',
  CREATE_CF_ACCOUNT = 'CREATE_CF_ACCOUNT',
  CREATE_DOMAIN = 'CREATE_DOMAIN',
  EDIT_TRANSLATION = 'EDIT_TRANSLATION',
  DOMAIN_ACTIONS = 'DOMAIN_ACTIONS',
  ADD_FOOTER_MENU_ITEM = 'ADD_FOOTER_MENU_ITEM',
  EDIT_FOOTER_MENU_ITEM = 'EDIT_FOOTER_MENU_ITEM',
  DELETE_FOOTER_MENU_ITEM = 'DELETE_FOOTER_MENU_ITEM',
  MEDIA_ITEM_VIEW = 'MEDIA_ITEM_VIEW',
  DELETE_BLOG_ITEM = 'DELETE_BLOG_ITEM',
  DELETE_PAGE_ITEM = 'DELETE_PAGE_ITEM',
  CREATE_STAFF = 'CREATE_STAFF',
  CREATE_TOURNAMENT = 'CREATE_TOURNAMENT',
  APPROVE_REJECT_TOURNAMENT_WINNER = 'APPROVE_REJECT_TOURNAMENT_WINNER',
  EDIT_PERMISSION = 'EDIT_PERMISSION',
  APPLY_PERMISSION_GROUP = 'APPLY_PERMISSION_GROUP',
}

interface Dependency<TSchema> {
  field: keyof TSchema;
  condition: (value: TSchema[keyof TSchema]) => boolean;
  action: 'hidden' | 'menuItems';
  value?: unknown;
  filter?: (item: unknown, dependencyValue: PathValue<TSchema, Path<TSchema>>) => boolean;
}

export interface FieldType<TSchema> {
  name: Path<TSchema>;
  type: InputType;
  description?: string;
  label: string;
  helpText?: string;
  required?: boolean;
  hidden?: boolean;
  menuItems?: { label: string; value: string | number; name?: string }[];
  multiline?: boolean;
  size?: ResponsiveStyleValue<GridSize>;
  direction?: 'row' | 'column';
  width?: string | number;
  trueLabel?: string;
  falseLabel?: string;
  value?: string | string[] | number | boolean | undefined;
  id?: number;
  inputProps?: InputBaseComponentProps | undefined;
  numberProps?: {
    minAmount?: number;
    maxAmount?: number;
    preventMaxAmount?: boolean;
    currency?: string;
    isPercentage?: boolean;
    acceptDecimals?: boolean;
    allowedDecimals?: number;
    allowSideEffectChanges?: boolean;
  };
  endAdornment?: ReactNode;
  dependencies?: Dependency<TSchema>[];
  disabled?: boolean;
  customKey?: string;
  recommendedDimensions?: string;
  placeholder?: string;
  dir?: 'ltr' | 'rtl';
  image?:
    | {
        url: string;
        alt?: string;
      }
    | undefined;
  regex?: RegExp;
  readOnly?: boolean;
}
export interface CustomConfigInputProps {
  name: string;
  type: InputType;
  description?: string;
  label: string;
  helpText?: string;
  required?: boolean;
  hidden?: boolean;
  menuItems?: { label: string; value: string | number; name?: string }[];
  multiline?: boolean;
  size?: ResponsiveStyleValue<GridSize>;
  direction?: 'row' | 'column';
  width?: string | number;
  trueLabel?: string;
  falseLabel?: string;
  value?: string | string[] | number | boolean | Date | undefined;
  defaultValue?: string | string[] | number | boolean | undefined;
  id?: number;
  inputProps?: InputBaseComponentProps | undefined;
  numberProps?: {
    minAmount?: number;
    maxAmount?: number;
    preventMaxAmount?: boolean;
    currency?: string;
    isPercentage?: boolean;
    acceptDecimals?: boolean;
    allowedDecimals?: number;
    allowSideEffectChanges?: boolean;
  };
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  customKey?: string;
  recommendedDimensions?: string;
  placeholder?: string;
  dir?: 'ltr' | 'rtl';
  image?:
    | {
        url: string;
        alt?: string;
      }
    | undefined;
  regex?: RegExp;
  readOnly?: boolean;
  zod?: z.ZodTypeAny;
  error?: string | undefined;
  herlperText?: string | undefined;
}

export enum InputType {
  INPUT = 'INPUT',
  NUMBER = 'NUMBER',
  NUMBER_ARRAY = 'NUMBER_ARRAY',
  MULTILINE_INPUT = 'MULTILINE_INPUT',
  SELECT = 'SELECT',
  ARRAY = 'ARRAY',
  BOOLEAN = 'BOOLEAN',
  MULTISELECT = 'MULTISELECT',
  PASSWORD = 'PASSWORD',
  AUTO_COMPLETE = 'AUTO_COMPLETE',
  AUTO_COMPLETE_V2 = 'AUTO_COMPLETE_V2',
  AUTO_COMPLETE_V2_FREE_SOLO = 'AUTO_COMPLETE_V2_FREE_SOLO',
  DATE = 'DATE',
  ETHEREUM_ADDRESS = 'ETHEREUM_ADDRESS',
  EDITOR = 'EDITOR',
  BUTTON_GROUP = 'BUTTON_GROUP',
  DATE_TIME = 'DATE_TIME',
  PAGE_PICKER = 'PAGE_PICKER',
  EDITOR_V2 = 'EDITOR_V2',
  DATE_PICKER = 'DATE_PICKER',
  IMAGE = 'IMAGE',
  CHECKBOX = 'CHECKBOX',
  DATE_TIME_PICKER = 'DATE_TIME_PICKER',
}

export enum TableList {
  ADMINS = 'ADMINS',
  BRANDS = 'BRANDS',
  PLAYERS = 'PLAYERS',
  BETS = 'BETS',
  TRANSACTIONS = 'TRANSACTIONS',
  CHANNELS = 'CHANNELS',
  GAMES = 'GAMES',
  DOCUMENTS = 'DOCUMENTS',
  OPERATOR_GAMES = 'OPERATOR_GAMES',
  SUPPORT_TICKETS = 'SUPPORT_TICKETS',
  AFFILIATES = 'AFFILIATES',
  NOTIFICATIONS = 'NOTIFICATIONS',
  FLOWS = 'FLOWS',
  OPERATORS = 'OPERATORS',
  USER_ACTIVITY = 'USER_ACTIVITY',
  PLAYER_ACTIVITY = 'PLAYER_ACTIVITY',
  DEPOSIT_LIST = 'DEPOSIT_LIST',
  DEPOSIT_PENDING = 'DEPOSIT_PENDING',
  DEPOSIT_ORDERS = 'DEPOSIT_ORDERS',
  DEPOSIT_GATEWAYS = 'DEPOSIT_GATEWAYS',
  WITHDRAW_LIST = 'WITHDRAW_LIST',
  WITHDRAW_PENDING = 'WITHDRAW_PENDING',
  WITHDRAW_ORDERS = 'WITHDRAW_ORDERS',
  WITHDRAW_GATEWAYS = 'WITHDRAW_GATEWAYS',
  PERMISSIONS = 'PERMISSIONS',
  DASHBOARD = 'DASHBOARD',
  PAGES = 'PAGES',
  CMS_MENUS = 'CMS_MENUS',
  CMS_SLIDES = 'CMS_SLIDES',
  CMS_SLIDERS = 'CMS_SLIDERS',
  GALLERY_GRID = 'GALLERY_GRID',
  GAMES_REPORT = 'GAMES_REPORT',
  GAME_OPERATOR_REPORT = 'GAME_OPERATOR_REPORT',
  OPERATOR_BILLING = 'OPERATOR_BILLING',
  DNS_RECORDS = 'DNS_RECORDS',
  AUTOMATION_LOGS = 'AUTOMATION_LOGS',
  BANK_ACCOUNTS = 'BANK_ACCOUNTS',
  PLAYERS_USERNAMES = 'PLAYERS_USERNAMES',
  TOKENS = 'TOKENS',
  DOMAINS = 'DOMAINS',
  SPORT_BONUS = 'SPORT_BONUS',
  PLAYER_APPLIED_FREEBETS = 'PLAYER_APPLIED_FREEBETS',
  ROLES = 'ROLES',
  TRANSLATION = 'TRANSLATION',
  AFFILIATE_REPORTS = 'AFFILIATE_REPORTS',
  BONUS_DEFINITION = 'BONUS_DEFINITION',
  CALLBACK_URLS = 'CALLBACK_URLS',
  LOGOUT_URLS = 'LOGOUT_URLS',
  ORIGIN_URLS = 'ORIGIN_URLS',
  AFD_CUSTOM_DOMAINS = 'AFD_CUSTOM_DOMAINS',
  INVOICE_LIST = 'INVOICE_LIST',
  APP_SERVICES = 'APP_SERVICES',
  PLAYER_CASHBACK = 'PLAYER_CASHBACK',
  CF_ACCOUNTS = 'CF_ACCOUNTS',
  BONUS_RELEASE = 'BONUS_RELEASE',
  STAFF = 'STAFF',
}

// export enum WalletType {
//   TOMANI = 'TOMANI',
//   CRYPTO = 'CRYPTO',
//   MINIPAY = 'MINIPAY',
// }

export interface LanguageProps {
  lng: string;
}

export interface GeneralConfig {
  id?: number;
  availableLanguages?: string;
  brand?: Brand;
  defaultLanguage?: string;
  createdAt?: Date;
  updatedAt?: Date;
  publishedAt?: Date;
  locale?: string;
  availableCurrencies?: string;
  defaultCurrency?: string;
  showGameSlider?: boolean;
  showGameBanner?: boolean;
  showBannerOne?: boolean;
  showBannerTwo?: boolean;
  siteTitle?: string;
  allowActiveCurrencyChange?: boolean;
  showCustomSupportModal?: boolean;
  crispWebsiteId?: string;
  telegramLoginURL?: string;
  googleLoginURL?: string;
  telegramBotId?: string;
  environment?: 'PROD' | 'STAGE' | 'TEST' | 'DEV';
  showComingSoon?: boolean;
  funLaunchUrl?: string;
  showFunLaunch?: boolean;
  translations?: GeneralTranslation;
}

export type GeneralTranslation = Record<string, unknown>;

export interface Brand {
  logo?: Asset;
  logoMobile?: Asset;
  socialMediaUrls?: {
    id: number;
    instagram?: string;
    linkedIn?: string;
    telegram?: string;
    youtube?: string;
  };
  licenseLogos?: Asset[];
}

export interface Asset {
  id: number;
  name: string;
  alternativeText: null;
  caption: null;
  width: number;
  height: number;
  formats: Formats;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: null;
  provider: string;
  provider_metadata: null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface Formats {
  thumbnail?: ImageFormat;
  medium?: ImageFormat;
  small?: ImageFormat;
}

export interface ImageFormat {
  name: string;
  hash: string;
  ext: string;
  mime: string;
  path: null;
  width: number;
  height: number;
  size: number;
  url: string;
}

export interface Game {
  id: number;
  title: string;
  gameId: string;
  extraData?: object | null;
  slug: string;
  tags: string | null;
  coverImg: Asset;
  provider: string;
  order?: number | null;
}

export interface ColumnType {
  id?: { content: string; onClick?: () => void };
  player?: ColumnPlayerInfo;
  operator?: {
    opId: string;
    opTitle?: string;
    parentOpId?: string;
    parentOpTitle?: string;
    tableItem?: boolean;
    showParentOperator?: boolean;
  };
  channel?: {
    channelLink: string;
    channelId: string;
    channelTitle?: string;
    channelIcon?: string;
  };
  text?: {
    tooltip?: string;
    content?: string;
    subtitle?: string;
    copyText?: boolean;
    strikethrough?: boolean;
    hidden?: boolean;
    dir?: 'LTR' | 'RTL';
    capitalize?: boolean;
  };
  wallet?: { component?: ReactNode; content?: ReactNode };
  time?: Date;
  component?: ReactNode;
  amountWithCurrency?: {
    amount: number;
    currency: string;
    showColor?: boolean;
    showNegativeColor?: boolean;
    subtitle?: string;
    showIcon?: boolean;
    valueInUSD?: number;
    showInUSD?: boolean;
    hideOriginalCurrency?: boolean;
    strikethrough?: boolean;
    extraData?: ReactNode;
    isBonus?: boolean;
    reverseColor?: boolean;
    bonusEligibility?: boolean | null;
    count?: number;
    color?: string;
    isLoading?: boolean;
    preventTrimming?: boolean;
  };

  winOdds?: number;
  boolean?: boolean;

  detailsDrawer?: { drawerState: GeneralDrawer; onClick?: () => void };
  detailsModal?: { modalState: GeneralModal; onClick?: () => void };
  status?: string;
  link?: string;
}

export interface RowProps {
  id: string;
  row: ColumnProps[];
  dark?: boolean;
  background?: string;
}

export interface ColumnProps {
  type: ColumnType;
  wide?: boolean | undefined;
  dark?: boolean;
  textAlign?: 'start' | 'center' | 'end';

  hideOnMobile?: boolean;
  header?: boolean;
  shrink?: boolean;
  target?: string;
  className?: string;
  fixedWidth?: number;
  colSpan?: number;
  showFilterButton?: boolean;
  disableGutter?: boolean;
}

export interface HeaderConfig {
  label: string;
  key?: string;
  sortable?: boolean;
  fixedWidth?: number;
  hideOnMobile?: boolean;
  align?: 'start' | 'center' | 'end';
}

export interface DynamicTableProps {
  headers: HeaderConfig[];
  rows: RowProps[];
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  pagination?: {
    count: number;
    remaining: number;
    page: number;
    limit: number;
  };
  hidePagination?: boolean;
  maxHeight?: number;
  isLoading?: boolean;
  lastFetchTime?: Date | undefined;
  quickFilters?: ReactNode;
}

export interface Currency {
  active: string;
  code: string;
  decimals: number;
  acceptedDecimals: number;
  isLocked: boolean;
  priority: number;
  symbol: string;
  currency: string;
  title: string;
  type: string;
  inUSD: number;
  oneUSD: number;
  inEUR: number;
  oneEUR: number;
}

export interface ExchangeRate {
  active: boolean;
  code: string;
  decimals: number;
  symbol: string;
  currency: string;
  title: string;
  type: string;
  inUSD: number;
  oneUSD: number;
  cmcId: number;
}

export type ExchangeRatesList = Record<string, ExchangeRate>;

export interface ExchangeRatesResponse {
  updatedAt: string;
  exchangeRates: ExchangeRatesList;
}
export interface AdminLabelProps {
  label: ReactNode;
  position?: 'start' | 'center' | 'end';
  sortable?: boolean;
  sortKey?: string;
  description?: ReactNode;
  descriptionType?: HelpInfoType;
  minWidth?: number;
  colSpan?: number;
  hiddenKey?: string;
}

export enum HelpInfoType {
  TOOLTIP = 'TOOLTIP',
  MODAL = 'MODAL',
}

export enum Token {
  CUSD = 'CUSD',
  CELO = 'CELO',
  LIF3 = 'LIF3',
  L3USD = 'L3USD',
  MATIC = 'MATIC',
}

export enum GameTag {
  HOT = 'HOT',
  NEW = 'NEW',
  LIVE = 'LIVE',
  TRENDING = 'TRENDING',
}

export enum GameCategory {
  SLOTS = 'Slots',
  TABLE_GAMES = 'Table games',
  LIVE_CASINO = 'Live Casino',
  JACKPOTS = 'Jackpots',
  OTHER = 'Other',
  SCRATCH_CARDS = 'Scratch Cards',
  VIDEO_POKER = 'Video Poker',
  ROULETTE = 'Roulette',
  FAST_GAME = 'FAST_GAME',
  CRASH_GAMES = 'Crash Games',
}

export interface Referral {
  refId: string;
  channelId: string;
  timestamp: number;
  isUsed: boolean;
}

export enum CryptoWallet {
  LIF3 = 'LIF3',
  MINIPAY = 'MINIPAY',
  METAMASK = 'METAMASK',
}

export enum Networks {
  ETHEREUM_MAIN = 'Ethereum',
  POLYGON_MAIN = 'Polygon',
  POLYGON_TEST = 'Polygon TestNet',
}

export interface CasinoBetsData {
  gameId: string;
  playerCode: string;
  placedAt: number;
  betAmount: number;
  winOdds: number;
  winAmount: number;
  imageUrl?: string;
  title?: string;
}

export enum CurrenciesTab {
  FIAT = 'FIAT',
  CRYPTO = 'CRYPTO',
}

export enum GameDetailsTab {
  DETAILS = 'DETAILS',
  SETTINGS = 'SETTINGS',
  JURISDICTIONS = 'JURISDICTIONS',
  AVAILABLE_OPERATORS = 'AVAILABLE_OPERATORS',
  CURRENCIES = 'CURRENCIES',
  ACTIVITY_LOGS = 'ACTIVITY_LOGS',
}

export enum BetTab {
  DETAILS = 'DETAILS',
  JSON = 'JSON',
  ADMIN_NOTES = 'ADMIN_NOTES',
  TECH_SUPPORT = 'TECH_SUPPORT',
}

export enum TransactionTab {
  DETAILS = 'DETAILS',
  JSON = 'JSON',
  ADMIN_NOTES = 'ADMIN_NOTES',
  VERIFY_HISTORY = 'VERIFY_HISTORY',
  LATEST_DEPOSITS = 'LATEST_DEPOSITS',
  LATEST_WITHDRAWS = 'LATEST_WITHDRAWS',
  SA_ACTIONS = 'SA_ACTIONS',
}
export enum BillingRecordTab {
  DETAILS = 'DETAILS',
  DETAILSV2 = 'DETAILS_V2',
  JSON = 'JSON',
}

export interface OperatorBrand {
  brandId?: string;
  brandTitle: string;
  domains: string[];
  theme: string;
}

type TTLParam = number;
type RecordTagsParam = string[];

export type RecordCreateParams = ARecord | AAAARecord;

// Define each record type directly (example for A and AAAA records)
export interface ARecord {
  zone_id: string;
  content: string;
  name: string;
  type: 'A';
  proxied?: boolean;
  ttl?: TTLParam;
  tags?: RecordTagsParam;
}

export interface AAAARecord {
  zone_id: string;
  content: string;
  name: string;
  type: 'AAAA';
  proxied?: boolean;
  ttl?: TTLParam;
  tags?: RecordTagsParam;
}

export interface AddDnsDto {
  type: 'A' | 'AAAA' | 'CNAME' | 'TXT';
  name: string;
  content: string;
  zoneId: string;
  cfToken: string;
  proxied?: boolean;
}
export interface AddDnsRuleDto {
  name: string;
  zoneId: string;
  cfToken: string;
}

export interface DeleteDnsDto {
  cfToken: string;
  recordId: string;
  zoneId: string;
}
export interface DeleteCnameRecordDto {
  cfToken: string;
  zoneId: string;
}

export interface TranslationItem {
  rowKey: string;
  [key: string]: string;
}

export interface AssociationParams {
  resourceGroupName: string;
  profileName: string;
  endpointShortName: string;
  clientRoute: string;
  customDomainId: string;
}

export interface Irole {
  _id: string;
  name: string;
  permissions: unknown;
}

export interface TranslationTableRow {
  rowKey: string;
  [key: string]: unknown;
}

export interface CFAccount {
  title: string;
  accountId: string;
  token: string;
}

export interface PasswordValidation {
  minLength: boolean;
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
  typesMatched: number;
}

export enum DomainActions {
  CF_DELETE = 'CF_DELETE',
  DB_DELETE = 'DB_DELETE',
  AFD_DELETE = 'AFD_DELETE',
  ASSOCIATE = 'ASSOCIATE',
  SYNC_CF = 'SYNC_CF',
  SET_PRIMARY = 'SET_PRIMARY',
}

export interface IStep {
  title: string;
  description?: string;
  tab: NewBrandTab;
  content: ReactNode;
  isCompleted: boolean;
}

export interface ColumnPlayerInfo {
  playerCode: string;
  playerId?: string;
  profile?: PlayerProfile;
  isTest?: boolean;
  isVerified?: boolean;
  username?: string;
}

export interface Row {
  label: { text?: string | number; component?: ReactNode };
  value: {
    text?: string | number;
    component?: ReactNode;
    link?: { href: string; text?: string };
    player?: ColumnPlayerInfo;
  };
  description?: string | string[];
  showCopyBtn?: boolean;
  multiline?: boolean;
  maxRows?: number;
  type?: 'text' | 'number' | 'password';
  name?: string;
  visible?: boolean;
  handleChange?: (value: string) => void;
}

export interface UnifiedSession {
  authMethod: 'nextauth' | 'microsoft';
  authToken?: string;
  externalToken?: string;
  user: AppJWTPayload;
}
export interface UnifiedSessionV2 {
  authMethod: 'nextauth' | 'microsoft';
  accessToken: string;
  refreshToken: string;
  user: UserSession;
}

export type TxName =
  | 'AFFILIATE'
  | 'AFFILIATE_PROFIT'
  | 'AFFILIATE_CPA_COMMISSION'
  | 'AFFILIATE_CPR_COMMISSION'
  | 'AFFILIATE_PROFIT_WITHDRAW'
  | 'AFFILIATE_REVENUE_SHARE';

export type TxSubtype = 'AFF_PROFIT' | 'AFF_CPA' | 'AFF_CPR' | 'AFF_REV_WITHDRAW' | 'AFF_REV_SHARE';
